import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  block?: boolean;
  children: ReactNode;
}

function classes({ variant = 'secondary', size = 'md', block }: CommonProps, extra?: string) {
  return [styles.button, styles[variant], styles[size], block ? styles.block : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

export function Button({
  variant,
  size,
  icon,
  block,
  children,
  className,
  type = 'button',
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={classes({ variant, size, block, children }, className)} data-variant={variant ?? 'secondary'} {...rest}>
      {icon}
      <span>{children}</span>
    </button>
  );
}

export function ButtonLink({
  variant,
  size,
  icon,
  block,
  children,
  className,
  ...rest
}: CommonProps & LinkProps) {
  return (
    <Link
      className={classes({ variant, size, block, children }, className as string | undefined)}
      data-variant={variant ?? 'secondary'}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}

/** Plain anchor (tel:, external navigation apps) styled as a button. */
export function ButtonAnchor({
  variant,
  size,
  icon,
  block,
  children,
  className,
  ...rest
}: CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={classes({ variant, size, block, children }, className)} data-variant={variant ?? 'secondary'} {...rest}>
      {icon}
      <span>{children}</span>
    </a>
  );
}
